import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import clsx from 'clsx'

import type {
  DayDigestSetting,
  NotificationKind,
  NotificationRecord,
  PushDevice,
  ScheduledNotificationKind,
  SchedulerRunResult,
} from '../../lib/data/types'
import { SCHEDULED_NOTIFICATION_KINDS } from '../../lib/data/types'
import {
  useClock,
  useDisablePush,
  useEnablePush,
  useNotificationLog,
  useNotificationSettings,
  usePushDevices,
  usePushPublicKey,
  usePushState,
  useRemovePushDevice,
  useSendTestNotification,
  useUpdateNotificationSettings,
} from '../../lib/data'
import { Link } from 'react-router-dom'

import { PERMISSION_DENIED_MESSAGE, UNSUPPORTED_MESSAGE } from '../../lib/push'
import { alertStamp, usePhoneAlerts, type PhoneAlerts } from '../../lib/native-notify'
import { Section, StatusPill } from '../connections/parts'
import { dayClockPhrase } from '../connections/labels'
import { ApiPath, FactCard, FactRow, NoteRow } from './parts'
import { formatShortDate, minutesToClock, minutesToInputTime, type ISODate } from '@app/shared'
import { clockInstant, localDayOf, localMinutesOfDay } from '../../lib/time'

// ============================================================================
// THE PIECES OF THE NOTIFICATIONS SCREEN.
//
// NO MOCKUP SOURCE — the handoff draws the settings nav's six rows and none of the screens behind
// them. So the geometry here is lifted from the two settings screens that DO exist (Connections
// and Appearance) and from components/settings/parts.tsx, not invented.
//
// THE ONE RULE EVERY PIECE BELOW KEEPS. A control on this screen is either connected to something
// that sends, or it is not there. The screen this replaced existed to say "LifeOS sends nothing",
// and the failure mode it warned about — a switch the owner remembers turning on, wondering for a
// month why nothing arrived — is still the failure mode. So: the switch is disabled and says why
// when the browser has blocked notifications; the whole panel says so when the deployment has no
// signing keys; and nothing here ever claims a send it did not see the API report.
// ============================================================================

/**
 * A REAL SWITCH — `role="switch"` on a button, the same shape the Connections screen's data-mode
 * switch uses, so the two settings screens do not grow two idioms for the same control.
 *
 * `disabled` is a real disabled button rather than a styled-off one: a switch that looks
 * pressable and refuses is worse than one that visibly cannot be pressed, and `reason` is what a
 * screen reader gets instead of the silence.
 */
export function Switch({
  checked,
  disabled,
  label,
  reason,
  onChange,
}: {
  checked: boolean
  disabled?: boolean
  label: string
  /** Why it cannot be pressed. Required whenever `disabled` — never a bare dead control. */
  reason?: string
  onChange: (next: boolean) => void
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      title={disabled ? reason : undefined}
      onClick={() => onChange(!checked)}
      className={clsx(
        'flex h-[22px] w-[38px] flex-none rounded-pill p-[3px]',
        'transition-colors duration-[var(--dur-base)] ease-[var(--ease-out)]',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring',
        checked ? 'justify-end bg-ink' : 'justify-start bg-fill',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
      )}
    >
      <span className="size-4 rounded-full bg-surface" aria-hidden />
    </button>
  )
}

/**
 * A titled row with a sentence under it and a control on the right — the settings row idiom.
 *
 * `compact` STACKS IT, and it is a layout prop rather than a second component because the row is
 * the same row: same copy, same control, same order down the accessibility tree. At 390px the
 * side-by-side arrangement leaves the "What gets sent" rows about 150px for a title next to a
 * lead field and a switch, and a settings label that wraps to four words per line is how a screen
 * stops being readable. Above the fold it changes nothing — the desktop passes no prop at all.
 */
export function ControlRow({
  title,
  detail,
  compact,
  children,
}: {
  title: string
  detail: ReactNode
  compact?: boolean
  children: ReactNode
}) {
  return (
    <div
      className={clsx(
        'flex px-[15px] py-[13px]',
        compact ? 'flex-col items-start gap-[10px]' : 'items-center gap-3',
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-[4px]">
        <div className="text-[12px] font-medium leading-none text-ink">{title}</div>
        <div className="text-[10.5px] font-normal leading-[1.45] text-microcopy text-pretty">
          {detail}
        </div>
      </div>
      {children}
    </div>
  )
}

// ── The four kinds ──────────────────────────────────────────────────────────

/**
 * WHAT EACH KIND ACTUALLY DOES, in the owner's own terms. These sentences are the contract the
 * Worker keeps (apps/worker/src/lib/notifications.ts), not a description of an intention: a
 * reminder fires at the instant it was set for, a block fires at the offsets chosen ON the block,
 * a class fires on the one lead below, and Google gets one lead because a Google reminder does
 * not travel down an ICS feed.
 */
export const KIND_COPY: Record<ScheduledNotificationKind, { label: string; detail: string }> = {
  task_reminder: {
    label: 'Reminders',
    detail:
      'At the time you set. A reminder with only a due day sends nothing — a day is not a time, ' +
      'and LifeOS will not pick an hour for you.',
  },
  calendar_block: {
    label: 'Calendar blocks',
    detail: 'Before they start, at the offsets you chose on the block. None unless you add one.',
  },
  gcal_event: {
    label: 'Google Calendar events',
    detail:
      'One lead for all of them: Google’s own per-event reminders do not travel down the feed it ' +
      'shares, so this is a setting rather than something LifeOS knows.',
  },
  class_start: {
    label: 'Class starting',
    detail: 'Before each scheduled class, with a tap that opens the recorder for that lecture.',
  },
}

/** What a log row's kind is called. `test` is included; the four above are not repeated. */
export const LOG_KIND_LABEL: Record<NotificationKind, string> = {
  task_reminder: 'Reminder',
  calendar_block: 'Calendar block',
  gcal_event: 'Google event',
  class_start: 'Class starting',
  test: 'Test',
}

// ── The lead fields ─────────────────────────────────────────────────────────

/** Four weeks, the Worker's own ceiling (`MAX_REMINDER_MINUTES`). */
export const MAX_LEAD_MINUTES = 40320

/**
 * A NUMBER OF MINUTES, SAVED ON BLUR — never on every keystroke.
 *
 * THE UNDO CONTRACT (CLAUDE.md → the ship gate's own list of what it cannot see). A controlled
 * `value=` input that is rewritten by a server response mid-typing kills the browser's native
 * undo stack after one autosave cycle. So the field's value is LOCAL state while it is being
 * typed in, the write happens when focus leaves or Enter is pressed, and the server's echo only
 * ever arrives between edits.
 *
 * An empty or out-of-range field does not save. It says what the range is and puts the stored
 * value back — a lead the owner cannot see is worse than a rejected edit.
 */
export function LeadField({
  label,
  minutes,
  onSave,
  disabled,
}: {
  label: string
  minutes: number
  onSave: (next: number) => void
  disabled?: boolean
}) {
  const [text, setText] = useState(String(minutes))
  const editing = useRef(false)

  // The stored value wins whenever this field is not the one being edited — a settings read that
  // answered after another device changed it should be visible here, and never mid-keystroke.
  useEffect(() => {
    if (!editing.current) setText(String(minutes))
  }, [minutes])

  const commit = () => {
    editing.current = false
    const next = Number(text.trim())
    if (!Number.isInteger(next) || next < 0 || next > MAX_LEAD_MINUTES) {
      setText(String(minutes))
      return
    }
    if (next !== minutes) onSave(next)
  }

  return (
    <label className="flex items-center gap-[9px]">
      <span className="text-[10.5px] font-normal leading-none text-microcopy">{label}</span>
      <input
        type="number"
        min={0}
        max={MAX_LEAD_MINUTES}
        step={1}
        inputMode="numeric"
        disabled={disabled}
        value={text}
        aria-label={label}
        onFocus={() => {
          editing.current = true
        }}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur()
        }}
        className="mo-field w-[72px] rounded-button border border-line-2 bg-surface px-[8px] py-[6px] text-[11.5px] leading-none text-ink outline-none disabled:opacity-50"
      />
      <span className="text-[10.5px] font-normal leading-none text-microcopy">min before</span>
    </label>
  )
}

// ── Devices ─────────────────────────────────────────────────────────────────

/**
 * ONE SUBSCRIBED BROWSER.
 *
 * THE NAME IS THE OWNER'S OR THERE IS NONE. The Worker never derives a label from the user agent
 * and neither does this row: an unnamed device says so, and the user-agent string sits underneath
 * as the raw fact it is. "Galaxy S24" printed as a name the app invented would be the app
 * claiming knowledge it was never given.
 *
 * `lastSuccessAt` NULL IS NOT A FAILURE. It is what a subscription that has never been pushed to
 * honestly looks like, and it says exactly that.
 */
export function DeviceRow({
  device,
  isThisBrowser,
  onRemove,
  removing,
  today,
}: {
  device: PushDevice
  isThisBrowser: boolean
  onRemove: () => void
  removing: boolean
  today: ISODate | undefined
}) {
  const disabled = Boolean(device.disabledAt)
  return (
    <div className="flex items-start gap-3 px-[15px] py-[12px]">
      <div className="flex min-w-0 flex-1 flex-col gap-[5px]">
        <div className="flex items-center gap-[8px]">
          <span className="text-[12px] font-medium leading-none text-ink">
            {device.label ?? 'Unnamed browser'}
          </span>
          {isThisBrowser && (
            <span className="rounded-pill bg-fill px-[7px] py-[3px] text-[9.5px] font-semibold uppercase leading-none tracking-[0.06em] text-ink-2">
              This browser
            </span>
          )}
          {disabled && <StatusPill tone="danger" label="DISABLED" />}
        </div>
        <div className="text-[10.5px] font-normal leading-[1.45] text-microcopy">
          {device.lastSuccessAt && today
            ? `Last delivered ${dayClockPhrase(device.lastSuccessAt, today)}`
            : 'Nothing has been delivered to it yet'}
          {today ? ` · added ${dayClockPhrase(device.createdAt, today)}` : ''}
        </div>
        {device.disabledReason && (
          <div className="text-[10.5px] font-normal leading-[1.45] text-danger-2 text-pretty">
            {device.disabledReason}
          </div>
        )}
        {device.userAgent && (
          <div className="truncate text-[10px] font-normal leading-[1.4] text-faint">
            {device.userAgent}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onRemove}
        disabled={removing}
        style={{ '--mo-rule': 'var(--color-danger-2)' } as React.CSSProperties}
        className="mo-ink mo-press flex-none rounded-pill px-[11px] py-[6px] text-[11px] font-semibold leading-none text-danger-2 disabled:opacity-40"
      >
        {removing ? 'Removing…' : 'Remove'}
      </button>
    </div>
  )
}

// ── The log ─────────────────────────────────────────────────────────────────

const STATUS_TONE = {
  sent: 'success',
  failed: 'danger',
  skipped: 'warning',
  queued: 'neutral',
} as const

/**
 * ONE LINE OF THE LOG — what LifeOS decided to say, when it was for, and what happened to it.
 *
 * THE ERROR SENTENCE IS SHOWN IN FULL for a `skipped` or `failed` row, because that sentence is
 * the whole reason the row is readable: "No browser is subscribed to notifications yet" and
 * "every browser refused it" are different diagnoses, and a status word alone cannot tell them
 * apart.
 *
 * `compact` UNPICKS THE HANGING INDENT, and nothing else. The wide row aligns its "For …" line
 * under the title by reserving the kind column's width twice — 104px of label, 113px of padding.
 * At 390px that indent is a third of the screen, and the sentence it pushes over is the one that
 * says WHEN the notification was for. So on a phone the kind label sits on its own line above the
 * title and both lines start at the gutter. Same three facts, same order, no truncation.
 */
export function LogRow({
  row,
  today,
  compact,
}: {
  row: NotificationRecord
  today: ISODate | undefined
  compact?: boolean
}) {
  const explain = row.error && (row.status === 'skipped' || row.status === 'failed')
  const indent = compact ? '' : 'pl-[113px]'
  return (
    <div className="flex flex-col gap-[5px] px-[15px] py-[11px]">
      {compact && (
        <span className="text-[9.5px] font-semibold uppercase leading-[1.5] tracking-[0.06em] text-microcopy">
          {LOG_KIND_LABEL[row.kind]}
        </span>
      )}
      <div className="flex items-center gap-[9px]">
        {!compact && (
          <span className="w-[104px] flex-none text-[9.5px] font-semibold uppercase leading-[1.5] tracking-[0.06em] text-microcopy">
            {LOG_KIND_LABEL[row.kind]}
          </span>
        )}
        <span
          className={clsx(
            'min-w-0 flex-1 text-[11.5px] font-medium leading-[1.5] text-ink-2',
            // The wide row truncates because its columns are fixed; the narrow one wraps,
            // because a title cut at 190px is a title that was not shown.
            compact ? 'text-pretty' : 'truncate',
          )}
        >
          {row.title}
        </span>
        <StatusPill tone={STATUS_TONE[row.status]} label={row.status.toUpperCase()} />
      </div>
      <div className={clsx(indent, 'text-[10.5px] font-normal leading-[1.45] text-microcopy')}>
        {today ? `For ${dayClockPhrase(row.fireAt, today)}` : `For ${row.fireAt}`}
        {row.attempts > 1 ? ` · ${row.attempts} attempts` : ''}
      </div>
      {explain && (
        <div
          className={clsx(indent, 'text-[10.5px] font-normal leading-[1.45] text-ink-3 text-pretty')}
        >
          {row.error}
        </div>
      )}
    </div>
  )
}

// ── The checker's heartbeat ─────────────────────────────────────────────────

/** Past this, a per-minute checker has missed at least two runs, and the line says so. */
export const CHECKER_LATE_MS = 3 * 60_000

// `clockInstant` lives in lib/time.ts now (the Reminders note measures against it too); it is
// re-exported here so this file's callers and tests keep their import.
export { clockInstant }

/** '12 min' · '3 hours' · '2 days' — how long a stopped checker has been stopped, never rounded down to zero. */
function silencePhrase(ms: number): string {
  const minutes = Math.max(1, Math.floor(ms / 60_000))
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  if (hours < 48) return `${hours} ${hours === 1 ? 'hour' : 'hours'}`
  const days = Math.floor(hours / 24)
  return `${days} days`
}

/**
 * THE ONE LINE THAT SAYS WHETHER THE CHECKER IS ALIVE (OPS-1a).
 *
 * The per-minute notification cron stamps the end of every run; the settings read hands the stamp
 * back. This turns it into words, and each branch is a different fact:
 *
 *   field absent   → no line at all. An older Worker does not send it, and absent is not "never".
 *   null           → "The checker has never run".
 *   under 3 min    → "Checked 2:41 PM" — or, if that run could not finish, its own sentence.
 *   older          → "Last checked 2:41 PM — the checker has not run for 12 min".
 *
 * `nowMs` is THE APP CLOCK (`useClock`), never a render-time `Date.now()`: it is what demo mode
 * pins to its seeded morning, and it is read and refreshed the same way the settings are, so a
 * page left open does not start accusing the checker of being late on its own. It is minute-
 * grained, so a stamp up to a minute "ahead" of it is simply fresh. The stamp prints 12-hour
 * (`minutesToClock`), with the day in front only when it is not today — a 2:41 PM from yesterday
 * is exactly the thing that must not look fresh.
 */
export function checkerLine(
  lastRunAt: string | null | undefined,
  result: SchedulerRunResult | null | undefined,
  nowMs: number,
  timeZone?: string,
): { text: string; late: boolean } | null {
  if (lastRunAt === undefined) return null
  if (lastRunAt === null) return { text: 'The checker has never run', late: true }
  const ranMs = Date.parse(lastRunAt)
  if (!Number.isFinite(ranMs)) return null

  const clock = minutesToClock(localMinutesOfDay(lastRunAt, timeZone))
  const ranDay = localDayOf(lastRunAt, timeZone)
  const today = localDayOf(new Date(nowMs).toISOString(), timeZone)
  const stamp = ranDay === today ? clock : `${formatShortDate(ranDay)}, ${clock}`

  const age = nowMs - ranMs
  if (age >= CHECKER_LATE_MS) {
    return {
      text: `Last checked ${stamp} — the checker has not run for ${silencePhrase(age)}`,
      late: true,
    }
  }
  if (result?.error) {
    return { text: `Checked ${stamp}, but that check could not finish: ${result.error}`, late: true }
  }
  return { text: `Checked ${stamp}`, late: false }
}

// ── The screen itself ───────────────────────────────────────────────────────

/** The log is a screenful, not a dump. Thirty rows is more than a day of them. */
export const LOG_LIMIT = 30

/** Why a test cannot go anywhere yet — said once, on the button and under the card. */
export const NOTHING_SUBSCRIBED_REASON =
  'No browser is subscribed yet, so there is nowhere to send a test. Turn notifications on above first.'

/**
 * THE NOTIFICATIONS SCREEN, MINUS ITS CHROME — five sections, and every state named in the block
 * at the top of this file.
 *
 * WHY IT LIVES HERE RATHER THAN IN routes/Notifications.tsx, WHICH IS WHERE IT WAS BORN. Web Push
 * is subscribed PER BROWSER: the switch below registers the device it is pressed on, and no other.
 * So the one setting in the app that the desktop cannot change on the owner's behalf was, until
 * this commit, reachable only from the desktop — the phone routed `/settings/*` to DesktopOnly and
 * there was no way in at all. Moving the body out of the route makes the phone render THE SAME
 * COMPONENT rather than a second copy of it, which is the only arrangement in which the two cannot
 * drift: a fork would have meant maintaining two device lists, two logs and two sets of honest
 * refusals, and the second one would have been wrong within a month.
 *
 * `compact` is a width, not a product. It stacks two rows and drops one hanging indent
 * (`ControlRow`, `LogRow` above); it hides nothing, adds nothing, and changes no copy.
 */
export function NotificationsSettings({ compact }: { compact?: boolean }) {
  const clock = useClock()
  const today = clock.data?.date

  const state = usePushState()
  const publicKey = usePushPublicKey()
  const devices = usePushDevices()
  const settings = useNotificationSettings()
  const log = useNotificationLog({ limit: LOG_LIMIT })
  // Rides on the settings read this screen already makes — no fetch of its own — and is measured
  // against the app clock, so it waits for both.
  const checker =
    settings.data && clock.data
      ? checkerLine(
          settings.data.schedulerLastRunAt,
          settings.data.schedulerLastResult,
          clockInstant(clock.data.date, clock.data.minutesIntoDay),
        )
      : null

  const enable = useEnablePush()
  const disable = useDisablePush()
  const removeDevice = useRemovePushDevice()
  const test = useSendTestNotification()
  const saveSettings = useUpdateNotificationSettings()

  const [label, setLabel] = useState('')

  // Inside the Android app the phone rings its own alarms; in a browser tab this stays `browser`
  // and the screen is exactly what it was.
  const phone = usePhoneAlerts()

  const supported = state.data?.supported ?? false
  const permission = state.data?.permission ?? 'unsupported'
  const enabled = state.data?.enabled ?? false
  const myEndpoint = state.data?.endpoint ?? null

  // The Worker's own sentence when this deployment has no VAPID keys, or the transport's when the
  // API could not be reached at all. Either way it is printed as written.
  const keyProblem =
    publicKey.isError && publicKey.error instanceof Error ? publicKey.error.message : null

  const switchBlocked = !supported || permission === 'denied' || Boolean(keyProblem)
  const switchReason = !supported
    ? UNSUPPORTED_MESSAGE
    : permission === 'denied'
      ? PERMISSION_DENIED_MESSAGE
      : (keyProblem ?? undefined)

  // ONLY ONCE THE LIST HAS ANSWERED. `devices.data` is undefined while the read is in flight, and
  // an empty list is a fact about the account, not a loading state — disabling the test button on
  // a query that has not returned would be the app guessing.
  const nothingSubscribed = devices.isSuccess && (devices.data ?? []).length === 0

  const pressError =
    enable.error instanceof Error
      ? enable.error.message
      : disable.error instanceof Error
        ? disable.error.message
        : null

  return (
    <>
      <Section label="This device">
        {/*
         * INSIDE THE ANDROID APP the yellow "no Web Push" box was true and useless: the app is a
         * WebView, and the phone now sets its own alarms instead (lib/native-notify.ts). So there
         * the box is replaced by what the phone is actually doing. In a browser tab, unchanged.
         */}
        {phone.alerts.kind !== 'browser' ? (
          <PhoneAlertsCard
            alerts={phone.alerts}
            checking={phone.checking}
            onCheckNow={phone.checkNow}
            compact={compact}
          />
        ) : (
          !supported && <NoteRow tone="warning">{UNSUPPORTED_MESSAGE}</NoteRow>
        )}
        {keyProblem && <NoteRow tone="warning">{keyProblem}</NoteRow>}

        <FactCard>
          <ControlRow
            compact={compact}
            title="Notifications on this browser"
            detail={
              enabled
                ? 'This browser is subscribed. Turning it off removes it from the list below.'
                : 'Turning this on asks the browser for permission, then registers this browser with LifeOS.'
            }
          >
            <div className="flex flex-none items-center gap-[10px]">
              {/*
               * THE BUTTON EXISTS BECAUSE THE SWITCH WAS MISSED. On the desk the row is wide, the
               * switch sits at the far right of it, and the owner pressed "Send a test" without
               * ever seeing the control that would have made a test go anywhere. A switch is the
               * right shape for OFF — it is the thing that is currently on — but ON is an action,
               * so while this browser is not subscribed the action is also a button that says
               * what it does. Denied gets neither: the row keeps the disabled switch and the
               * sentence naming where the browser's own setting lives.
               */}
              {!enabled && permission !== 'denied' && (
                <button
                  type="button"
                  onClick={() => enable.mutate(label)}
                  disabled={switchBlocked || enable.isPending || disable.isPending}
                  title={switchBlocked ? switchReason : undefined}
                  style={{ '--mo-rule': 'var(--color-surface)' } as CSSProperties}
                  className="mo-ink mo-press flex-none rounded-pill bg-ink px-[13px] py-[7px] text-[11px] font-semibold leading-none text-surface disabled:opacity-40"
                >
                  {enable.isPending ? 'Turning on…' : 'Turn on notifications'}
                </button>
              )}
              <Switch
                label="Notifications on this browser"
                checked={enabled}
                disabled={switchBlocked || enable.isPending || disable.isPending}
                reason={switchReason}
                onChange={(next) => (next ? enable.mutate(label) : disable.mutate())}
              />
            </div>
          </ControlRow>

          {!enabled && supported && permission !== 'denied' && (
            <ControlRow
              compact={compact}
              title="Name this device"
              detail="Optional, and nothing is guessed: a device with no name is listed as one. It is only ever used in the list below."
            >
              <input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Galaxy S24 · Chrome"
                aria-label="Name this device"
                className={clsx(
                  'mo-field rounded-button border border-line-2 bg-surface px-[9px] py-[7px] text-[11.5px] leading-none text-ink outline-none placeholder:text-faint',
                  // Fixed on the desk, fluid on the phone: a 190px field next to a stacked label
                  // is a field with 170px of dead air beside it.
                  compact ? 'w-full' : 'w-[190px]',
                )}
              />
            </ControlRow>
          )}

          <ControlRow
            compact={compact}
            title="Browser permission"
            detail="Read off the browser every time this screen draws, never remembered — you can change it in the site settings while this tab is open."
          >
            <StatusPill
              tone={
                permission === 'granted' ? 'success' : permission === 'denied' ? 'danger' : 'neutral'
              }
              label={
                permission === 'granted'
                  ? 'GRANTED'
                  : permission === 'denied'
                    ? 'DENIED'
                    : permission === 'default'
                      ? 'NEVER ASKED'
                      : 'NOT OFFERED'
              }
            />
          </ControlRow>
        </FactCard>

        {permission === 'denied' && <NoteRow tone="warning">{PERMISSION_DENIED_MESSAGE}</NoteRow>}
        {pressError && <NoteRow tone="warning">{pressError}</NoteRow>}

        <NoteRow>
          On the phone this only works while Chrome is allowed to run in the background: Android
          Settings → Apps → Chrome → Battery → Unrestricted. A phone that puts Chrome to sleep
          delivers the notification whenever it next wakes, which for a reminder is the same as not
          delivering it.
        </NoteRow>

        {/*
         * WHAT LIFEOS CANNOT DECIDE. Every notification asks for a short vibration, but Android
         * gives sound and vibration to the per-site channel the phone owns, and a web app cannot
         * override it — so the honest answer is where that setting lives, not a promise about a
         * buzz. Same for the full-screen alarm a native calendar can throw: the web has no such
         * thing, and saying "we'll add it" about a platform capability would be inventing one.
         */}
        <NoteRow>
          Whether it makes a sound or vibrates is the phone's to decide, not LifeOS's: every
          notification asks for a short buzz, and Android answers from the per-site channel at
          Android Settings → Apps → Chrome (or the installed LifeOS app) → Notifications →
          lifeos-3hu.pages.dev → sound and vibration. A web app also cannot take over the screen
          the way Google Calendar does — that needs a native app, which is planned and does not
          exist yet. The nearest thing here is that calendar blocks, Google events and class
          starts stay on screen until you dismiss them.
        </NoteRow>
      </Section>

      <Section label="Devices">
        {devices.isLoading ? (
          <NoteRow>Reading the list…</NoteRow>
        ) : devices.isError ? (
          <NoteRow tone="warning">
            {devices.error instanceof Error
              ? devices.error.message
              : 'The list of subscribed browsers could not be read.'}
          </NoteRow>
        ) : (devices.data ?? []).length === 0 ? (
          <NoteRow>
            No browser is subscribed yet, so nothing would be sent anywhere. That is an empty list,
            not a failure — turn the switch above on to put this one in it.
          </NoteRow>
        ) : (
          <FactCard>
            {(devices.data ?? []).map((device) => (
              <DeviceRow
                key={device.id}
                device={device}
                isThisBrowser={Boolean(myEndpoint) && device.endpoint === myEndpoint}
                today={today}
                removing={removeDevice.isPending && removeDevice.variables?.id === device.id}
                onRemove={() => removeDevice.mutate({ id: device.id, endpoint: device.endpoint })}
              />
            ))}
          </FactCard>
        )}
        {removeDevice.error instanceof Error && (
          <NoteRow tone="warning">{removeDevice.error.message}</NoteRow>
        )}
      </Section>

      <Section label="Prove it">
        <FactCard>
          <ControlRow
            compact={compact}
            title="Send a test notification"
            detail={
              'It goes down the same path the scheduler uses — one row in the log, then a push per ' +
              'subscribed browser — so a test that arrives proves the real thing would.'
            }
          >
            {/*
             * SECONDARY WHILE THERE IS NOWHERE TO SEND. With no subscribed browser this button is
             * the most prominent thing on the screen and the one press that cannot work — which
             * is how the owner came to press it instead of the switch. So when the device list is
             * empty it is not the primary action: it reads as the quiet one, it is really
             * disabled rather than live-looking, and the sentence under the card says why.
             */}
            <button
              type="button"
              onClick={() => test.mutate()}
              disabled={test.isPending || nothingSubscribed}
              title={nothingSubscribed ? NOTHING_SUBSCRIBED_REASON : undefined}
              style={
                {
                  '--mo-rule': nothingSubscribed ? 'var(--color-ink)' : 'var(--color-surface)',
                } as CSSProperties
              }
              className={clsx(
                'mo-ink mo-press flex-none rounded-pill px-[13px] py-[7px] text-[11px] font-semibold leading-none disabled:opacity-40',
                nothingSubscribed
                  ? 'border border-line-2 bg-surface text-ink-2'
                  : 'bg-ink text-surface',
              )}
            >
              {test.isPending ? 'Sending…' : 'Send a test'}
            </button>
          </ControlRow>
        </FactCard>

        {nothingSubscribed && <NoteRow>{NOTHING_SUBSCRIBED_REASON}</NoteRow>}

        {test.isError && (
          <NoteRow tone="warning">
            {test.error instanceof Error ? test.error.message : 'The test send failed.'}
          </NoteRow>
        )}

        {test.data && (
          <NoteRow tone={test.data.sent > 0 ? 'neutral' : 'warning'}>
            {test.data.outcomes.length === 0
              ? 'Nothing is subscribed, so the test went nowhere. It is in the log as a test all the same.'
              : `Accepted by ${test.data.sent} of ${test.data.outcomes.length} ${
                  test.data.outcomes.length === 1 ? 'browser' : 'browsers'
                }.`}
            {test.data.outcomes
              .filter((outcome) => !outcome.ok)
              .map((outcome) => (
                <span key={outcome.subscriptionId} className="mt-[6px] block">
                  {outcome.label ?? 'Unnamed browser'}: {outcome.error ?? 'not accepted'}
                  {outcome.disabled ? ' It has been disabled; re-enable it on that device.' : ''}
                </span>
              ))}
          </NoteRow>
        )}
      </Section>

      <Section label="What gets sent">
        {settings.isError ? (
          <NoteRow tone="warning">
            {settings.error instanceof Error
              ? settings.error.message
              : 'The notification settings could not be read.'}
          </NoteRow>
        ) : !settings.data ? (
          <NoteRow>Reading the settings…</NoteRow>
        ) : (
          <>
          {/* THE DAY PAGE'S DAILY NOTIFICATION — drawn exactly as the day-page Keyboard board draws
              it ("Settings › Notifications"). Only when the Worker sends `dayDigest`: a switch with
              no setting behind it is the control this screen promises never to show. */}
          {settings.data.dayDigest && (
            <DayDigestRow
              setting={settings.data.dayDigest}
              disabled={saveSettings.isPending}
              onSave={(dayDigest) => saveSettings.mutate({ dayDigest })}
            />
          )}
          <FactCard>
            {SCHEDULED_NOTIFICATION_KINDS.map((kind) => (
              <ControlRow
                key={kind}
                compact={compact}
                title={KIND_COPY[kind].label}
                detail={KIND_COPY[kind].detail}
              >
                <div className="flex flex-none items-center gap-[12px]">
                  {kind === 'class_start' && (
                    <LeadField
                      label="Lead"
                      minutes={settings.data.classLeadMinutes}
                      disabled={saveSettings.isPending}
                      onSave={(classLeadMinutes) => saveSettings.mutate({ classLeadMinutes })}
                    />
                  )}
                  {kind === 'gcal_event' && (
                    <LeadField
                      label="Lead"
                      minutes={settings.data.gcalLeadMinutes}
                      disabled={saveSettings.isPending}
                      onSave={(gcalLeadMinutes) => saveSettings.mutate({ gcalLeadMinutes })}
                    />
                  )}
                  <Switch
                    label={KIND_COPY[kind].label}
                    checked={settings.data.kindsEnabled[kind]}
                    disabled={saveSettings.isPending}
                    onChange={(next) => saveSettings.mutate({ kindsEnabled: { [kind]: next } })}
                  />
                </div>
              </ControlRow>
            ))}
          </FactCard>
          </>
        )}
        {saveSettings.error instanceof Error && (
          <NoteRow tone="warning">{saveSettings.error.message}</NoteRow>
        )}
        <p className="text-[11.5px] font-normal leading-[1.5] text-ink-3 text-pretty">
          Every switch here is also readable and writable over the API —{' '}
          <ApiPath>/api/notifications/settings</ApiPath> — like everything else in LifeOS.
        </p>
      </Section>

      <Section label="What was sent">
        {checker && (
          <p
            className={clsx(
              'text-[10.5px] font-normal leading-[1.45] text-pretty',
              checker.late ? 'text-warning-2' : 'text-microcopy',
            )}
          >
            {checker.text}
          </p>
        )}
        {log.isError ? (
          <NoteRow tone="warning">
            {log.error instanceof Error ? log.error.message : 'The log could not be read.'}
          </NoteRow>
        ) : (log.data ?? []).length === 0 ? (
          <NoteRow>
            Nothing has been decided on yet. Every notification LifeOS makes is written down before
            it is sent, so this is the answer to “did it tell me about the 9am?” once there is one.
          </NoteRow>
        ) : (
          <FactCard>
            {(log.data ?? []).map((row) => (
              <LogRow key={row.id} row={row} today={today} compact={compact} />
            ))}
          </FactCard>
        )}
        <p className="text-[11.5px] font-normal leading-[1.5] text-ink-3 text-pretty">
          The newest {LOG_LIMIT}, whatever happened to them. The same rows come back from{' '}
          <ApiPath>/api/notifications</ApiPath>, which is what lets chat answer the same question.
        </p>
      </Section>
    </>
  )
}

// ── The Day page's daily notification (day-page Keyboard board) ─────────────

/**
 * "Day page · What still needs you, once a day · at [13:00] · ●" — one card, as the board draws it.
 *
 * The time is LOCAL state while it is edited and saves on blur or Enter (the undo contract
 * LeadField keeps above): a server echo never rewrites a field mid-edit. The switch is the board's
 * own 36×20 live-green switch rather than this screen's ink one, because the board is the spec.
 */
export function DayDigestRow({
  setting,
  disabled,
  onSave,
}: {
  setting: DayDigestSetting
  disabled?: boolean
  onSave: (next: DayDigestSetting) => void
}) {
  const stored = minutesToInputTime(setting.hour * 60 + setting.minute)
  const [time, setTime] = useState(stored)
  const editing = useRef(false)
  useEffect(() => {
    if (!editing.current) setTime(stored)
  }, [stored])
  const commit = () => {
    editing.current = false
    const m = /^(\d{2}):(\d{2})$/.exec(time)
    if (!m) {
      setTime(stored)
      return
    }
    const hour = Number(m[1])
    const minute = Number(m[2])
    if (hour === setting.hour && minute === setting.minute) return
    onSave({ ...setting, hour, minute })
  }
  return (
    <div className="flex items-center gap-3 rounded-card border border-line bg-surface px-4 py-[13px] shadow-card">
      <div className="flex flex-1 flex-col gap-[3px]">
        <span className="text-[12.5px] font-semibold text-ink">Day page</span>
        <span className="text-[11px] text-microcopy">What still needs you, once a day</span>
      </div>
      <label className="flex items-center gap-[6px] text-[12px] text-ink-2">
        at
        <input
          type="time"
          value={time}
          aria-label="Notification time"
          disabled={disabled}
          onChange={(e) => {
            editing.current = true
            setTime(e.target.value)
          }}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit()
          }}
          className="h-7 rounded-[8px] border border-line-2 bg-surface px-[6px] font-[inherit] text-[12px] text-ink"
        />
      </label>
      <button
        type="button"
        role="switch"
        aria-checked={setting.enabled}
        aria-label="Day page notification"
        disabled={disabled}
        onClick={() => onSave({ ...setting, enabled: !setting.enabled })}
        className={clsx(
          'relative h-5 w-9 flex-none cursor-pointer rounded-pill border-0 p-0 transition-colors duration-[var(--dur-state)] ease-[var(--ease-ink)]',
          setting.enabled ? 'bg-success-dot' : 'bg-fill',
        )}
      >
        <span
          className={clsx('absolute top-[2px] size-4 rounded-full bg-on-dark transition-[left] duration-[var(--dur-state)] ease-[var(--ease-ink)]', setting.enabled ? 'left-[18px]' : 'left-[2px]')}
          aria-hidden
        />
      </button>
    </div>
  )
}

// ── Inside the Android app: the phone's own alarms ──────────────────────────

/** The sentence the card carries in every state that has a phone behind it. */
export const ENABLED_BY_UPLOADER =
  'Turning on the phone uploader (the device token on Recordings) is what enables it.'

/**
 * "THIS PHONE RINGS ITS OWN ALARMS" — what the screen shows inside the Android app instead of the
 * yellow "This browser has no Web Push" box.
 *
 * That box was true and useless there: the app is a WebView, it has no Web Push, and the owner had
 * no reminders on his phone at all (2026-09-25). The app now pulls its own next 24 hours from the
 * Worker and arms one exact alarm per item (lib/native-notify.ts; the plugin's AlertScheduler).
 * Every fact here comes from `notifystate`; an absent one is a dash with its reason; a phone that
 * cannot ring (no device token, an older build, notifications off) says so and where the fix is.
 */
export function PhoneAlertsCard({
  alerts,
  checking,
  onCheckNow,
  compact,
  nowMs = Date.now(),
}: {
  alerts: PhoneAlerts
  checking: boolean
  onCheckNow: () => void
  compact?: boolean
  nowMs?: number
}) {
  if (alerts.kind === 'browser') return null
  if (alerts.kind === 'asking') return <NoteRow>Asking this phone about its own alarms…</NoteRow>
  if (alerts.kind === 'unsupported') {
    return (
      <NoteRow tone="warning">
        This build of the LifeOS app cannot ring reminders on its own ({alerts.reason}). A newer
        build of the app can — until it is installed, nothing on this phone rings for reminders or
        calendar events.
      </NoteRow>
    )
  }

  const s = alerts.state
  return (
    <>
      <FactCard>
        <ControlRow
          compact={compact}
          title="This phone rings its own alarms"
          detail={
            s.enabled
              ? 'Reminders, calendar blocks and Google events for the next 24 hours, checked with ' +
                'LifeOS every 15 minutes and whenever the app opens or closes. They ring with LifeOS ' +
                `closed — Chrome is not involved. ${ENABLED_BY_UPLOADER}`
              : `Web Push does not reach this app, so the phone sets its own alarms instead. ${ENABLED_BY_UPLOADER}`
          }
        >
          {s.enabled && (
            <button
              type="button"
              onClick={onCheckNow}
              disabled={checking}
              style={{ '--mo-rule': 'var(--color-surface)' } as CSSProperties}
              className="mo-ink mo-press flex-none rounded-pill bg-ink px-[13px] py-[7px] text-[11px] font-semibold leading-none text-surface disabled:opacity-40"
            >
              {checking ? 'Checking…' : 'Check now'}
            </button>
          )}
        </ControlRow>
        {s.enabled && (
          <>
            <FactRow
              label="Last checked"
              value={s.lastPullAt ? alertStamp(s.lastPullAt, nowMs) : null}
              missingReason="The phone has not checked with LifeOS yet."
            />
            <FactRow label="Alarms set" value={String(s.armedCount)} />
            <FactRow
              label="Next alarm"
              value={
                s.nextFireAt
                  ? alertStamp(s.nextFireAt, nowMs)
                  : s.armedCount === 0 && s.lastPullAt
                    ? 'None in the next 24 hours'
                    : null
              }
              missingReason="The phone has not checked with LifeOS yet."
            />
            <FactRow label="Rang in the last day" value={String(s.firedLast24h)} />
          </>
        )}
      </FactCard>

      {!s.enabled && (
        <NoteRow tone="warning">
          This phone has no device token yet, so nothing rings here. Open{' '}
          <Link to="/school/recordings" className="font-medium text-ink underline underline-offset-2">
            Recordings
          </Link>{' '}
          and tap “Let the phone upload on its own” — the same token lets it ring reminders and
          calendar events.
        </NoteRow>
      )}
      {s.enabled && s.lastPullError && (
        <NoteRow tone="warning">The last check did not work: {s.lastPullError}</NoteRow>
      )}
      {s.notificationsAllowed === false && (
        <NoteRow tone="warning">
          Android has notifications for LifeOS switched off, so an alarm would go off with nothing
          to see or hear. Android Settings → Apps → LifeOS → Notifications turns them on (including
          “Reminders and calendar”).
        </NoteRow>
      )}
      {s.exactAlarms === false && (
        <NoteRow tone="warning">
          Android has not granted LifeOS exact alarms, so these may ring late. Android Settings →
          Apps → LifeOS → Alarms &amp; reminders turns it on.
        </NoteRow>
      )}
    </>
  )
}
